import dayjs, { Dayjs } from 'dayjs'
import { GeneratedContent } from '../ContentPlanning/SelectContent/SelectContent'
import { PostCount } from '../schemas/shared/captionCount'
import { PostingCadence } from '../ContentPlanning/ScheduleContent/ScheduleContent'
import {
  DayAndTime,
  TimeOfDay,
} from '../ContentPlanning/ScheduleContent/components/StartDateAndPostFrequency/StartDateAndPostFrequencyModal'

export const postCountToNumber = (value?: string) => {
  switch (value) {
    case PostCount.Five: {
      return 5
    }
    case PostCount.Ten: {
      return 10
    }
    case PostCount.Fifteen: {
      return 15
    }
    case PostCount.Thirty: {
      return 30
    }
    case PostCount.ThirtyWeekdays: {
      return 30
    }
    default: {
      return 10
    }
  }
}

export const daysToPostingCadence = (days: number[]) => {
  const daysStr = days.join('')

  if (daysStr.length === 1) return PostingCadence.Once

  switch (daysStr) {
    case '24':
      return PostingCadence.Twice
    case '135':
      return PostingCadence.Thrice
    case '12345':
      return PostingCadence.Weekdays
    case '0123456':
      return PostingCadence.Everyday
  }

  return PostingCadence.Custom
}

export const postCountToPostingCadence = (count?: PostCount): PostingCadence => {
  switch (count) {
    case PostCount.Five: {
      return PostingCadence.Once
    }
    case PostCount.Ten: {
      return PostingCadence.Twice
    }
    case PostCount.Fifteen: {
      return PostingCadence.Thrice
    }
    case PostCount.ThirtyWeekdays: {
      return PostingCadence.Weekdays
    }
    case PostCount.Thirty: {
      return PostingCadence.Everyday
    }
    default: {
      return PostingCadence.Once
    }
  }
}

export const postCountToDaysAndTimes = (count: PostCount): DayAndTime[] => {
  const cadence = postCountToPostingCadence(count)
  const time = { ofDay: TimeOfDay.Random }
  switch (cadence) {
    case PostingCadence.Once:
      return [{ day: dayjs().add(1, 'day').day(), time }]
    case PostingCadence.Twice:
      return [
        { day: 2, time },
        { day: 4, time },
      ]
    case PostingCadence.Thrice:
      return [
        { day: 1, time },
        { day: 3, time },
        { day: 5, time },
      ]
    case PostingCadence.Weekdays:
      return [
        { day: 1, time },
        { day: 2, time },
        { day: 3, time },
        { day: 4, time },
        { day: 5, time },
      ]
    case PostingCadence.Everyday:
      return [
        { day: 0, time },
        { day: 1, time },
        { day: 2, time },
        { day: 3, time },
        { day: 4, time },
        { day: 5, time },
        { day: 6, time },
      ]
    case PostingCadence.Custom:
      return [] // Adding this for TS. No-op
  }
}

const randomTimeBetween = (start: Dayjs, end: Dayjs) => {
  const randomHour = Math.floor(Math.random() * (end.hour() - start.hour()) + start.hour())
  const possibleMinutes = [0, 15, 30, 45]
  let minutes: number[] = []
  possibleMinutes.forEach((minute) => {
    if (randomHour > start.hour()) {
      minutes.push(minute)
      return
    }

    if (minute > start.minute()) {
      minutes.push(minute)
    }
  })
  const randomMinute = minutes[Math.floor(Math.random() * minutes.length)]

  return dayjs().hour(randomHour).minute(randomMinute)
}

export const scheduleContent = (content: GeneratedContent[], postingDays: DayAndTime[], startDate?: Dayjs) => {
  const today = dayjs().minute(0).second(0)
  let currentDay = startDate || today.add(7, 'day')

  const isPostableDay = (day: dayjs.Dayjs, validDays: number[]) => validDays.includes(day.day())

  const newContent = content.map((item) => {
    while (
      !isPostableDay(
        currentDay,
        postingDays.map((d) => d.day),
      )
    ) {
      currentDay = currentDay.add(1, 'day')
    }

    let postAt = currentDay

    const dayAndTime = postingDays.find((d) => d.day === currentDay.day())
    if (dayAndTime) {
      const now = dayjs()
      const isToday =
        now.date() === currentDay.date() && now.month() === currentDay.month() && now.year() === currentDay.year()

      switch (dayAndTime.time.ofDay) {
        case TimeOfDay.EarlyMorning:
          if (isToday) {
            // random time after now and before 9am
            const postAtTime = randomTimeBetween(now, now.hour(9).minute(0))
            postAt = postAt.hour(postAtTime.hour()).minute(postAtTime.minute())
          } else {
            // random time between 12am-9am
            const postAtTime = randomTimeBetween(now.hour(0).minute(0), now.hour(9).minute(0))
            postAt = postAt.hour(postAtTime.hour()).minute(postAtTime.minute())
          }
          break
        case TimeOfDay.Morning:
          if (isToday && now.hour() >= 9) {
            // random time after now and before 12pm
            const postAtTime = randomTimeBetween(now, now.hour(12).minute(0))
            postAt = postAt.hour(postAtTime.hour()).minute(postAtTime.minute())
          } else {
            // random time between 9am-12pm
            const postAtTime = randomTimeBetween(now.hour(9).minute(0), now.hour(12).minute(0))
            postAt = postAt.hour(postAtTime.hour()).minute(postAtTime.minute())
          }
          break
        case TimeOfDay.Afternoon:
          if (isToday && now.hour() >= 12) {
            // random time after now and before 5pm
            const postAtTime = randomTimeBetween(now, now.hour(16).minute(0))
            postAt = postAt.hour(postAtTime.hour()).minute(postAtTime.minute())
          } else {
            // random time between 12pm-5pm
            const postAtTime = randomTimeBetween(now.hour(12).minute(0), now.hour(17).minute(0))
            postAt = postAt.hour(postAtTime.hour()).minute(postAtTime.minute())
          }
          break
        case TimeOfDay.Evening:
          if (isToday && now.hour() >= 5) {
            // random time after now and before 9pm
            const postAtTime = randomTimeBetween(now, now.hour(20).minute(0))
            postAt = postAt.hour(postAtTime.hour()).minute(postAtTime.minute())
          } else {
            // random time between 5pm-9pm
            const postAtTime = randomTimeBetween(now.hour(17).minute(0), now.hour(21).minute(0))
            postAt = postAt.hour(postAtTime.hour()).minute(postAtTime.minute())
          }
          break
        case TimeOfDay.Night:
          if (isToday && now.hour() >= 9) {
            // random time after now and before 11:59pm
            const postAtTime = randomTimeBetween(now, now.hour(23).minute(59))
            postAt = postAt.hour(postAtTime.hour()).minute(postAtTime.minute())
          } else {
            // random time between 9pm-11:59pm
            const postAtTime = randomTimeBetween(now.hour(21).minute(0), now.hour(23).minute(59))
            postAt = postAt.hour(postAtTime.hour()).minute(postAtTime.minute())
          }
          break
        case TimeOfDay.Custom:
          let hour = dayAndTime.time.at?.hour
          const minute = dayAndTime.time.at?.minute
          const amOrPm = dayAndTime.time.at?.amOrPm

          if (hour && typeof minute === 'number' && amOrPm) {
            let actualHour = hour
            if (amOrPm === 'pm') {
              actualHour = hour + 12
            }
            postAt = postAt.hour(actualHour).minute(minute)
          }

          break
        case TimeOfDay.Random:
          // This shouldn't be an option if startDate is today
          const randomHour = Math.floor(Math.random() * 11) + 9
          const randomMinute = [0, 15, 30, 45][Math.floor(Math.random() * 4)]
          postAt = postAt.hour(randomHour).minute(randomMinute)
          break
      }
    }

    // Move to the next day for the next piece of content
    currentDay = currentDay.add(1, 'day')

    return { ...item, postAt }
  })

  return newContent
}
